/**
 * 도움말 — 한국어. 글만 있고 동작은 없다(동작은 quickStartTracks.ts).
 *
 * 주제(topics)는 두 곳이 같이 본다: 헤더 「도움말 › 사용 안내」 창과 어시스턴트의 `get_usage_guide` 도구.
 * 트랙(tracks)은 Quick Start 의 단계 글이다 — 단계 수와 순서는 동작 목록과 맞아야 한다(help.test.ts 가 고정).
 */
import type { HelpContent } from './types';

const ko: HelpContent = {
  lang: 'ko',

  topics: [
    {
      id: 'start',
      title: '시작하기',
      summary: '프로젝트를 고르고, 폴더 아래에 워크플로를 만들어 디자이너 캔버스에서 설계합니다.',
      steps: [
        '로그인 뒤 프로젝트를 고릅니다. 디자이너 툴바 맨 왼쪽 「프로젝트 › 변경」으로 다른 프로젝트로 옮깁니다. 같은 메뉴에 프로젝트 내보내기·불러오기·테라스트림에서 가져오기가 있습니다.',
        '디자이너 탭 왼쪽 탐색기: 프로젝트 › 폴더 › 워크플로. 프로젝트 줄의 + 로 워크플로를, 폴더 아이콘으로 폴더를 만듭니다.',
        '워크플로를 클릭하면 캔버스에 열립니다. 헤더 가운데 배지가 지금 열린 프로젝트 / 워크플로입니다.',
        '샘플 프로젝트의 「기본 샘플」 폴더에 샘플①~⑩ 이 있습니다. 마음껏 고쳐 보고, 필요하면 복제해서 쓰세요(워크플로 줄의 복제 아이콘).',
        '대시보드 탭은 프로젝트 전체의 워크플로 현황, 실행 탭은 배포된 워크플로의 실행·이력입니다. 편집은 디자이너에서만 합니다.',
      ],
    },
    {
      id: 'dfd',
      title: '캔버스와 DFD 규칙',
      summary: '왼쪽 툴박스의 노드를 캔버스로 끌어 놓고, 노드 오른쪽 점에서 다음 노드로 드래그해 잇습니다. 데이터 ↔ 처리가 번갈아야 합니다.',
      steps: [
        '툴박스 데이터(SAM파일·DB테이블·내부데이터)는 데이터 노드, 프로세스(데이터처리·필터·조인·집계·품질검사…)는 처리 노드, 컨트롤(분기·합류·반복그룹·서브플로)는 흐름 제어입니다.',
        'DFD 규칙: 데이터 노드끼리 직접 잇지 않습니다. 데이터 → 처리 → 데이터 순으로 번갈아 놓고, 흐름의 시작과 끝은 데이터 노드입니다. 캔버스 위 「DFD 검사」 줄이 적합/위반을 바로 알려 줍니다.',
        '연결선에 마우스를 올리면 가운데에 + 가 나타납니다 — 누르면 그 사이에 노드를 끼워 넣습니다. 툴박스에서 끌어 + 위에 놓아도 됩니다.',
        '연결선을 누르면 골라지고(빨간색) Delete 로 지웁니다. 선의 끝(화살표 쪽이나 시작 쪽)을 잡아 다른 노드에 놓으면 그 노드로 옮겨집니다 — 새로 이을 때와 같은 연결 규칙을 따르고, 되돌리기 한 번에 돌아옵니다.',
        '노드를 클릭하면 오른쪽 「노드 속성」 탭에 설정이 뜹니다. 이름·설정은 거기서 고칩니다. Delete 로 지웁니다.',
        '위반을 실무상 허용해야 하면 DFD 검사 줄에서 「예외 허용」을 누릅니다. 「예외 해제」로 되돌립니다.',
        '캔버스 상단의 워터마크는 프로젝트 / 워크플로 이름입니다. 오른쪽 위 아이콘으로 이미지·PDF 로 저장합니다.',
      ],
    },
    {
      id: 'data',
      title: '데이터 정의 (SAM파일·DB테이블)',
      summary: '헤더의 「데이터 정의」에서 파일·테이블을 등록하고, 같은 창에서 줄 오른쪽 ＋ 아이콘으로 워크플로에 노드를 넣습니다.',
      steps: [
        '데이터 정의 › SAM파일: 서버 폴더에서 파일을 고르고 구분자·헤더·인코딩·고정폭(fieldWidths)을 정합니다. 미리보기로 항목이 제대로 갈리는지 확인합니다. 헤더가 없으면 항목명(fieldNames)을 직접 적습니다.',
        '데이터 정의 › DB테이블: 연결(Airflow Connection) › 스키마 › 테이블을 고릅니다. 컬럼이 자동으로 읽힙니다.',
        '「데이터 정의」 창에서 정의 줄 오른쪽의 ＋ 아이콘을 누르거나 줄을 더블클릭하면 지금 워크플로에 SAM파일/DB테이블 노드가 생깁니다.',
        '노드 속성에서 mode(READ/WRITE)를 정합니다. 소스는 READ, 타깃은 WRITE 입니다.',
        '「서버 폴더」 메뉴에서 파일이 있는 허용 루트를 확인합니다.',
      ],
    },
    {
      id: 'innerData',
      title: "내부데이터 (중간 결과)",
      summary: "처리 결과를 파일로 남기지 않고 같은 실행 안의 다음 처리로 넘기는 데이터 노드입니다. 간편하지만 수명·메모리·값 형태에 제약이 있어, SAM 파일과 골라 씁니다.",
      steps: [
        "쓰는 자리: 처리와 처리 사이에는 데이터 노드가 있어야 합니다(DFD 규칙). 추출 → 내부데이터 → 필터·조인·적재처럼 잠깐 거쳐 가는 결과에 씁니다. 항목은 앞 처리의 출력에서 자동으로 채워지고, 속성의 「항목상세」에서 확인·선택합니다.",
        "수명: 그 실행 한 번 안에서만 있습니다. 다른 워크플로가 읽거나, 다음 날 실행이 이어 쓸 수 없습니다. 다시 쓸 결과는 SAM 파일이나 DB 테이블로 내려 두세요.",
        "메모리: 모든 행을 한꺼번에 메모리에 올립니다. 행마다 항목 이름이 함께 실려 원본 파일 크기의 몇 배를 차지합니다. 실행 화면의 「메모리」 열로 노드별 사용량을 봅니다.",
        "대량 전달: 결과가 기본 1만 행을 넘으면 실행 기록 대신 중간 파일(압축)로 넘깁니다. 워크플로 설정 › 「중간 데이터 전달」에서 기준 행 수와 방식(자동·항상 기록·파일·테이블)을 바꿉니다. 테이블 방식은 PostgreSQL 연결에서만 됩니다. 중간 파일은 기본 24시간 뒤 지워집니다.",
        "중간부터 다시 실행: 고른 노드부터 다시 돌릴 때 앞 단계의 내부데이터를 이어 받으려면 그 결과가 작거나(256KB 이하) 중간 파일이 아직 남아 있어야 합니다. 아니면 앞 노드부터 다시 돕니다.",
        "값 형태: 넘기는 동안 값이 이렇게 바뀝니다 — 소수가 있는 숫자는 실수(float)가 되어 자릿수가 긴 값(예: NUMBER(20,6))은 끝자리가 달라질 수 있습니다. 날짜·시각은 ISO 문자열(2026-10-20T14:35:00), BLOB 은 「base64:」 로 시작하는 문자열, CLOB 은 글자 그대로가 됩니다. 정수는 그대로입니다.",
        "확인: 파일로 남지 않으므로 노드를 고르고 속성의 「입력·출력 데이터」나 실행 화면의 입력·출력 보기로 봅니다. 큰 결과는 실행 기록에 남지 않아 실행이 끝난 뒤에는 볼 수 없을 수 있습니다.",
        "고르는 기준 — 이럴 때는 SAM 파일·DB 테이블이 낫습니다: 수십만 행 이상 / 다른 워크플로나 다음 실행에서 다시 쓰는 결과 / 금액처럼 모든 자릿수가 중요한 값 / 중간부터 다시 돌릴 일이 잦은 흐름 / 결과를 파일로 남겨 대조해야 할 때.",
      ],
    },
    {
      id: 'transform',
      title: '데이터처리와 매핑',
      summary: '데이터처리 노드가 소스를 읽어 타깃에 씁니다. 항목 매핑은 노드 속성의 매핑 창에서, 「매핑 제안」과 「✨ AI 보조」로 채웁니다.',
      steps: [
        '소스 데이터 → 데이터처리 → 타깃 데이터 순으로 잇습니다. 노드 속성에서 transformMode(MOVE/TRANSFORM)와 mode(WRITE/APPEND/UPSERT)를 정합니다.',
        '매핑 창: 타깃 항목마다 소스 항목 또는 식(expr)을 넣습니다. 「매핑 제안」이 이름·표준용어·타입으로 짝을 맞추고, 길이 절단·날짜 형식 차이를 경고합니다.',
        '「✨ AI 보조」는 이름만으로 못 맞춘 짝을 어시스턴트가 제안합니다. 근거 배지(이름·용어·순서·ai)를 보고 골라 반영합니다.',
        '매핑 설계서: 프로젝트 › 매핑 설계서에서 테이블 매핑·컬럼 매핑·코드 매핑을 그리드로 쓰거나, 양식 CSV 를 받아 채운 뒤 가져옵니다. 규칙유형은 직접·식·상수·기본값·코드·제외입니다. 「DB에서 채우기」는 타깃 컬럼을 늘어놓고 같은 이름의 소스를 잇고, 「검증」은 없는 컬럼·NOT NULL 누락·길이 축소·변환식 오류를 찾습니다. 저장할 때마다 판(v1, v2…)이 오릅니다. 노드의 매핑 창 옆 「설계서에서」로 매핑ID 를 고르면 그 판의 매핑이 노드에 들어가고, 설계서에 새 판이 생기면 노드에 「새 판 — 바뀐 컬럼 N개」가 보여 「반영」을 눌러야 바뀝니다. 실행 로그에 어느 판으로 돌았는지 남습니다.',
        '샘플 미리보기 표로 변환 결과를 먼저 봅니다. 대용량이면 useBulkLoader, LOB 는 lobMode 를 씁니다. DB→DB 이관의 LOB 은 inline 이 맞습니다 — file 은 값마다 파일을 만들어 수십만 건이면 디스크·백신 검사로 크게 느려집니다(문서·이미지를 파일로 꺼낼 때만). 벌크 로더는 BLOB 이 있으면 보통 적재로 바꿔 넣습니다. file 로 읽은 값을 DB 에 적재하면 경로가 아니라 파일 내용으로 되돌려 넣고, SAM 파일로 쓰면 경로가 남습니다. DB 원본을 DB(새로 쓰기·이어 쓰기)나 SAM 으로 옮길 때는 1만 행씩 읽는 대로 적재해 메모리를 적게 씁니다(UPSERT·벌크 로더는 통째로). Oracle 타깃은 「직접 경로 적재」 로 더 빨리 넣을 수 있습니다 — 묶음마다 커밋합니다. SAM 원본도 1만 행씩 읽는 대로 적재하고, SAM → Oracle 에 벌크 로더를 켜면 항목 변환이 없을 때 원본 파일을 SQL*Loader 에 바로 넘깁니다 — SAM 칸에 LOB 파일 경로가 있으면 SQL*Loader 가 그 파일을 직접 읽어 CLOB·BLOB 에 넣습니다(LOBFILE, 테라스트림과 같은 방식). SAM 값은 글자를 잃지 않을 때만 숫자로 읽습니다 — 코드값 01 은 01 그대로입니다.',
        '필터·조인·집계·정렬/중복제거·룩업·품질검사는 SAM파일·DB테이블에 바로 이어도 됩니다 — 실행할 때 그 원본을 직접 읽습니다. 데이터처리 결과를 받을 때는 사이에 내부데이터 노드를 둡니다. 조인은 먼저 이은 입력이 왼쪽입니다(SAM파일과 DB테이블을 섞어도 됩니다).',
      ],
    },
    {
      id: 'control',
      title: '분기·합류·반복·서브플로',
      summary: '분기 노드는 조건으로 흐름을 나누고 합류가 다시 모읍니다. 반복그룹·서브플로는 노드를 묶습니다.',
      steps: [
        '분기: 오른쪽 핸들이 TRUE, 아래 핸들(false-path)이 FALSE 입니다. 조건은 노드 속성의 condition(예: amount > 100000).',
        '합류: 건너뛴 경로가 있어도 이어서 실행되도록 trigger_rule 이 설정됩니다. 여러 갈래를 한 노드로 모읍니다.',
        '반복그룹·서브플로: 툴박스에서 끌어 놓으면 큰 상자가 생기고, 그 안에 노드를 놓으면 자식이 됩니다. DAG 에서는 TaskGroup 이 됩니다.',
        '실행 노드(SQL 명령어·셸·워크플로 실행)는 툴박스 「실행」 묶음에 있습니다. 워크플로 실행은 다른 워크플로를 태스크로 부릅니다.',
        'SQL 검증: SQL실행 노드의 「검증」(속성 패널·SQL 편집기)은 SQL 을 실행하지 않고 DB 에 해석만 시켜 문장마다 통과·오류·경고·건너뜀을 보여 줍니다 — 문법 오류, 없는 테이블·컬럼, 권한 부족을 미리 찾습니다. 문장을 누르면 편집기에서 그 자리를 고르고, 줄 번호에 색이 칠해집니다. DDL(CREATE·DROP·TRUNCATE 등)은 해석만 해도 실행되는 DB 가 있어 검증하지 않습니다. 앞 문장에서 만드는 테이블은 경고로 표시합니다. 실행 중에만 나는 오류(제약 위반·공간 부족·잠금)는 검증으로 잡을 수 없습니다. 검증할 때마다 「검증 기록」에 남습니다. SQL 을 고친 뒤 적용하거나 워크플로를 저장하면 검증할지 묻습니다(검증 후 저장 · 검증 없이 저장 · 취소).',
        'SQL 식별 표시: SQL실행 노드가 실행한 SQL 을 SQL 개발도구·DBA 가 알아볼 수 있게 합니다. Oracle 은 세션의 module(DAG id)·action(노드 이름 #문장번호)·client_info(실행 id)·client_identifier(실행한 사람)로, PostgreSQL 은 application_name 으로 늘 표시합니다(v$session·v$sql·pg_stat_activity 에서 봅니다). 또 SQL 끝에 /*openflow:workflow=…,node=…,stmt=…*/ 주석을 붙입니다 — SQL 문장이 바뀌므로 Plan Baseline·SQL Profile 을 쓰는 SQL 이면 워크플로 설정 › 상세 › SQL 식별 표시나 노드의 「SQL 끝에 식별 주석 붙이기」에서 끕니다. 다시 배포해야 반영됩니다.',
        '사용자 확인 노드는 실행을 멈추고 확인을 기다립니다(샘플④).',
      ],
    },
    {
      id: 'quality',
      title: '품질검사',
      summary: '적재 앞에 품질검사 노드를 두어 규칙(필수·형식·범위·유일)을 검사하고 위반 시 멈추거나 건너뜁니다.',
      steps: [
        '툴박스 프로세스 › 품질검사를 데이터 노드 뒤에 놓습니다. 노드 속성에서 규칙을 추가합니다.',
        '「규칙 제안」이 앞 노드의 컬럼·샘플을 보고 규칙(not null, 날짜 형식, 코드 값, 범위)을 제안합니다. 고르고 반영합니다.',
        'onViolation(FAIL/SKIP/WARN)과 maxViolationPct 로 위반 처리를 정합니다.',
        '위반 행은 reject-path 핸들로 따로 빼서 파일·테이블에 남길 수 있습니다.',
      ],
    },
    {
      id: 'compare',
      title: "데이터비교 (원천·타겟 값 대조)",
      summary: "두 데이터를 키로 짝지어 값 수준으로 비교하고, 차이를 SAM파일 레포트로 남깁니다. 이관·적재 결과 검증에 씁니다. 워크플로와 따로 돌아 비교가 실패해도 워크플로는 영향을 받지 않습니다.",
      steps: [
        "툴박스 프로세스 › 「데이터비교」를 놓으면 결과를 받을 SAM파일(비교결과 레포트) 노드가 함께 생기고 이어집니다. 레포트 경로는 그 SAM파일 노드에서 바꿉니다.",
        "비교할 데이터 둘(SAM파일·DB테이블·내부데이터)에서 데이터비교로 선을 잇습니다 — 점선으로 그려집니다(데이터가 흐르는 길이 아니라 「이 둘을 본다」). 속성의 「비교 대상」에서 원천·타겟을 고릅니다.",
        "범위/필터 조건: 쪽마다 적습니다(비우면 전체). DB 는 그 DB 의 SQL WHERE, SAM·내부데이터는 필터 노드 문법입니다. ${변수} 를 씁니다.",
        "비교 키: 원천 항목 = 타겟 항목 쌍입니다(여러 개 가능, 앞뒤 공백 무시). 키가 없으면 저장·배포할 때 막습니다.",
        "비교 로직: 「규칙으로 생성」은 원천에서 타겟까지의 흐름을 따라 데이터처리의 매핑 식을 합성해 항목마다 「기대값」 을 만듭니다(예: NAME ← upper(trim(CUST_NM))). 조인·집계를 만나거나 흐름이 없으면 이름으로 짝짓습니다. 무엇을 근거로 했는지 줄마다 적힙니다.",
        "「✨ AI 보조」는 항목 이름·타입과 흐름의 매핑·조건만 보내 비교 로직을 제안받습니다(표본 행은 보내지 않습니다 — 조건·식에 적힌 상수는 그대로 갑니다). 제안은 꺼진 채로 붙고 AI 표시가 달립니다 — 확인하고 켭니다. 없는 항목·쓸 수 없는 함수를 쓴 제안은 사유와 함께 뺍니다. 지시를 덧붙여 다시 받을 수 있습니다.",
        "규칙마다: 항목 또는 식, 공백 무시·대소문자 무시·숫자로(허용 오차)·날짜로. 체크를 끄면 그 항목은 비교하지 않습니다. 규칙이 하나도 없으면 이름이 같은 항목끼리 비교합니다.",
        "실행 시점: 「입력 데이터가 준비되면 바로」(입력을 만드는 처리가 끝난 뒤) 또는 「전체 워크플로가 끝난 뒤」(앞이 실패해도 돕니다). 비교 오류는 워크플로를 실패시키지 않고 레포트에 ERROR 로 남습니다.",
        "비교 방식: 기본은 메모리 비교입니다. 대량이면 「DB 안에서 비교」 — 양쪽이 같은 연결이어야 하고, SAM파일은 그 연결에 임시테이블(OF_CMP_…)로 올려 비교한 뒤 지웁니다. 식 규칙은 DB 안 비교에서 빠지고, 조건이 맞지 않으면 메모리로 바꿔 레포트에 알립니다.",
        "레포트(SAM): RESULT, KEY, COLUMN, SOURCE_VALUE, TARGET_VALUE. SUMMARY 줄에 상태(MATCH/DIFF/ERROR)·건수·항목별 차이 수, DIFF 는 값이 다른 항목, SOURCE_ONLY·TARGET_ONLY 는 한쪽에만 있는 키, DUP_KEY 는 키 중복입니다. 상세 줄은 상한(기본 10000)까지, 요약은 언제나 전체 기준입니다.",
      ],
    },
    {
      id: 'variables',
      title: '전역변수와 실행 매개변수',
      summary: '헤더 「전역변수 정의」에 값을 두고 노드 설정에서 ${이름} 으로 씁니다. 실행할 때 값을 바꿀 수 있습니다.',
      steps: [
        '전역변수 정의: 이름·기본값·설명. 노드 설정 문자열 안에서 ${이름} 으로 참조합니다(파일 경로·조건·SQL 등).',
        '실행 버튼을 누르면 매개변수 창이 떠서 이번 실행의 값을 바꿀 수 있습니다. Airflow 의 conf 로 넘어갑니다.',
        '실행 탭의 「실행」도 같은 창을 씁니다.',
      ],
    },
    {
      id: 'save',
      title: '저장·버전·되돌리기',
      summary: 'Save 로 QBase 에 저장되고 버전이 쌓입니다. 「버전」에서 예전 버전을 보고 되돌립니다. 편집 중엔 Ctrl+Z.',
      steps: [
        'Save(v번호): 캔버스를 저장하고 버전을 올립니다. 다른 워크플로로 옮겨도 편집 중인 캔버스는 브라우저에 남아 돌아오면 그대로입니다.',
        '「버전」: 버전 목록을 보고 특정 버전을 열거나 되돌립니다.',
        'Ctrl+Z 되돌리기, Ctrl+Y 다시실행 — 캔버스 오른쪽 위 도구 모음의 ↶ ↷ 와 같습니다. 어시스턴트 제안을 반영한 것도 한 번에 되돌아갑니다.',
        '워크플로 설정(탐색기에서 워크플로 옆 ⚙): 이름·폴더·설명과 스케줄(cron). 재시도·알림·중간 데이터는 「상세」를 펼쳐 정합니다.',
        '워크플로 줄의 복제 아이콘으로 사본을 만듭니다. 삭제는 소프트 삭제라 관리자가 되살릴 수 있습니다.',
      ],
    },
    {
      id: 'deploy',
      title: 'DAG 코드·배포·실행',
      summary: '「DAG 코드」로 변환 결과를 보고, 「Deploy」로 Airflow 에 올린 뒤 「실행」합니다. 실행 탭에서 이력·타임라인을 봅니다.',
      steps: [
        'DAG 코드: 지금 캔버스를 Airflow DAG 파이썬 코드로 변환해 보여 줍니다(배포하지 않음). 변환 오류도 여기서 먼저 봅니다. 데이터 노드는 태스크가 아니라 처리 태스크의 설정으로 접힙니다.',
        'Deploy: DAG 파일을 Airflow 에 올립니다. 스케줄은 워크플로 설정(탐색기 ⚙)의 cron 을 따릅니다. 배포 뒤 Airflow 가 읽는 데 몇 초 걸립니다.',
        '실행: 매개변수를 확인하고 트리거합니다. 「실행 이력」에서 태스크별 상태를 봅니다.',
        '실행 탭: 프로젝트의 배포된 워크플로 목록(실행 중 → 최근 종료 순), 실행 이력, 달력, 그리고 시간 구간에서 오래 돈 워크플로 타임라인. 여기서는 조회·실행만 하고 편집은 디자이너에서 합니다.',
        'Airflow 웹(기본 8082)에서 같은 DAG 를 볼 수 있습니다. DAG id 는 openflow_<워크플로id> 입니다.',
      ],
    },
    {
      id: 'wizard',
      title: '마법사와 템플릿',
      summary: '워크플로 마법사가 소스·타깃을 묻고 기본 흐름을 만들어 줍니다. 자주 쓰는 설정은 템플릿으로 둡니다.',
      steps: [
        '탐색기의 마법사 아이콘(⚡): 소스(SAM/DB)·타깃·처리 옵션을 고르면 노드와 연결을 만들어 줍니다. 「마법사 샘플」 폴더에 결과 예가 있습니다.',
        '헤더 「템플릿 정의」: 노드 설정 템플릿을 관리합니다.',
        '툴박스에서 노드 종류를 클릭하면, 그 종류에 등록된 인스턴스가 있을 때 캔버스 위 띠에 칩으로 나타나 끌어 놓을 수 있습니다.',
      ],
    },
    {
      id: 'migrate',
      title: '테라스트림 프로젝트 가져오기(이관)',
      summary: '툴바 「프로젝트 › 테라스트림에서 가져오기」에서 테라스트림 Designer 의 프로젝트 XML 을 워크플로로 바꿉니다. 변환만으로는 아무것도 저장하지 않고, 보고서를 본 뒤 「가져오기」로 지금 프로젝트에 워크플로를 만듭니다.',
      steps: [
        '디자이너 탭 툴바 맨 왼쪽 「프로젝트」 › 「테라스트림에서 가져오기」. 테라스트림 Designer 에서 내보낸 프로젝트 XML(또는 zip)을 고릅니다. 서로 부르는 프로젝트(다른 프로젝트 실행 블록)는 함께 고르면 실행 노드가 그 워크플로를 가리키게 이어집니다.',
        '옮기는 블록: 다른 프로젝트 실행 → 실행 노드(지연 시간은 「실행 전 대기」, 인자 $$[1]… 은 ${P1}…), 프로시저/SQL → SQL실행 노드, 언로드 → DB테이블 → 데이터처리 → SAM파일, 로드 → SAM파일 → 데이터처리(벌크 적재) → DB테이블, 메모 → 메모 노드. 실행 중단 표시는 노드의 실행 중단으로 옮깁니다. 모르는 블록은 메모 노드로 남기고 보고서에 「수동」으로 알립니다.',
        '「파일 경로 바꾸기」: 현장 경로를 이 서버 경로로 바꿉니다(한 줄에 하나, 예: /data01 => mig/data01). 「SAM 파일 인코딩」: 자동 감지 또는 직접 선택.',
        '「변환」을 누르면 보고서가 나옵니다 — 그대로 · 참고 · 확인 · 수동 · 오류. 이 서버에 없는 연결, 서버 폴더 밖에 쓰는 파일, 이번에 올리지 않은 프로젝트를 부르는 블록도 여기서 알려 줍니다. 「보고서 내려받기」로 저장해 둘 수 있습니다.',
        '연결의 비밀번호·접속 정보는 테라스트림 export 에 없습니다. 같은 ID 로 연결을 만들면 워크플로가 그대로 씁니다 — 무설치판 관리자는 변환 창의 「만들기」로 바로 만들고, 웹 버전은 Airflow 에서 만듭니다.',
        '워크플로를 둘 폴더를 고르고 「가져오기」 — 지금 열어 둔 프로젝트에 워크플로로 들어옵니다(프로젝트는 새로 만들지 않습니다). 가져온 워크플로는 저장·배포·실행 전에 노드의 「!」 표시와 DFD 검사를 확인하세요. 배포할 때도 없는 연결·경로를 다시 알려 줍니다.',
        '내부망 무설치판에서도 됩니다 — 변환은 AI 를 쓰지 않습니다. 실제 사례로 확인한 블록 종류는 docs/MIGRATION_TERASTREAM_CASES.md 에 있습니다.',
      ],
    },
    {
      id: 'ai',
      title: 'AI 어시스턴트',
      summary: '오른쪽 「AI」 탭. 말로 워크플로를 만들고 고치되, 어시스턴트는 제안만 하고 반영·배포·실행은 사용자가 합니다.',
      steps: [
        '"orders.csv 를 읽어 주문 테이블에 적재하는 흐름 만들어줘" 처럼 말하면 어시스턴트가 데이터 정의·연결·컬럼을 도구로 확인하고, 검증을 통과한 캔버스를 제안 카드로 보여 줍니다. 「반영」을 눌러야 캔버스가 바뀝니다(Ctrl+Z 로 되돌림).',
        '사용법을 물으면 안내를 근거로 답하고, 필요하면 샘플 워크플로를 열어 실제 화면을 가리키며 설명합니다.',
        '「도움말」이라고 쓰면 Quick Start / 주제별 안내 / 자유 질문 메뉴가 버튼으로 뜹니다.',
        '계정: 내 Claude/Codex 계정으로 로그인해 씁니다(계정 이름을 눌러 연결 설정). 서버 공용 키는 없습니다. 「대화」로 지난 대화를 다시 엽니다.',
        '무설치판은 AI 를 넣어 꾸린 판에서만, 그리고 인터넷에 닿을 때만 AI 버튼이 보입니다(내부망에서는 자동으로 감춥니다). 이유는 도움말 › 정보에 나옵니다. AI 가 없어도 다른 기능은 모두 그대로입니다.',
      ],
    },
    {
      id: 'shortcuts',
      title: '단축키',
      summary: 'Ctrl+Z 되돌리기 · Ctrl+Y 다시실행 · Ctrl+C/V 복사·붙여넣기 · Delete 삭제 · 휠 확대/축소 · 드래그 이동',
      steps: [
        'Ctrl+Z 되돌리기, Ctrl+Y 다시실행',
        'Ctrl+C / Ctrl+V 선택 노드 복사·붙여넣기(연결 포함), Delete/Backspace 선택 삭제',
        '마우스 휠 확대/축소, 빈 곳 드래그 이동, 캔버스 왼쪽 아래 ⤢ 로 전체 맞춤. 자물쇠 아이콘은 배율 자동/수동 전환',
        'AI 입력창: Enter 전송, Shift+Enter 줄바꿈',
      ],
    },
  ],

  labels: {
    quick: 'Quick Start — 단계별로 만들어 보기',
    otherTrack: '다른 트랙 해 보기',
    topics: '주제별 안내 보기',
    free: '자유롭게 질문하기',
    next: '다음',
    stop: '그만',
    did: '해봤어요, 다음',
    doForMe: '대신 해줘',
    sample: '샘플로 보기',
    other: '다른 주제',
    menu: '도움말 메뉴',
    done: '끝',
  },

  ui: {
    menu: '무엇을 도와드릴까요? 아래에서 고르거나 그냥 질문해도 됩니다.',
    pickTopic: '주제를 고르세요.',
    pickTrack: 'Quick Start 는 세 트랙입니다. 어느 것이든 빈 워크플로에서 시작해 저장·배포·실행·결과 확인까지 실제로 돌려 봅니다.\n입문: SAM 파일을 DB 에 적재하는 가장 기본 흐름 (10단계)\n중급: DB 에서 읽어 조건으로 분기하고 합류해 적재 (11단계)\n고급: 품질검사 규칙으로 통과·위반을 갈라 DB 와 파일에 (10단계)',
    stopped: '도움말을 마칩니다. 언제든 「도움말」이라고 부르세요.',
    freeAsk: '네, 무엇이든 물어보세요. 워크플로 설계·데이터 확인·사용법 모두 됩니다.',
    freeAskShort: '네, 무엇이든 물어보세요.',
    trackFinished: 'Quick Start {{track}} 트랙을 마쳤습니다.',
    openingSample: '샘플 워크플로 「{{name}}」을 엽니다. 화면의 캔버스로 설명하겠습니다.',
    explainSample: '지금 화면에 열린 샘플 워크플로 「{{name}}」의 실제 노드·연결을 가리키며 「{{title}}」을 짧게 설명해줘. 먼저 get_usage_guide("{{id}}") 로 안내를 읽고, 마크다운 제목 없이 5줄 이내로, 마지막에 사용자가 직접 해 볼 행동 하나를 suggest_actions 버튼으로 제안해줘. 캔버스는 이미 열려 있으니 open_sample_workflow 를 다시 부르지 마.',
    doneDefault: '완료했습니다.',
    quickDone: '수고하셨습니다. 이제 자유롭게 만들어 보세요.',
    save: {
      title: '저장',
      text: '캔버스 오른쪽 위 도구 모음의 「Save」(디스켓 아이콘)를 누르면 QBase 에 저장되고 버전 번호가 올라갑니다. 실수했으면 Ctrl+Z (도구 모음 ↶) — 「대신 해줘」로 넣은 것도 같은 방법으로 되돌립니다.\n「버전」에서 예전 버전을 보고 되돌릴 수 있고, 탐색기에서 워크플로 옆 ⚙ 를 누르면 이름·폴더·스케줄(cron)을 정합니다.',
      doneText: 'Save 아이콘 위의 v번호가 올라간 것을 확인해 보세요.',
    },
    deploy: {
      title: 'DAG 코드와 배포',
      text: '「DAG 코드」를 누르면 지금 캔버스가 Airflow DAG 파이썬 코드로 어떻게 변환되는지 보입니다(배포하지 않음). 데이터 노드는 태스크가 아니라 처리 태스크의 설정으로 접힙니다.\n「Deploy」를 누르면 그 DAG 파일이 Airflow 에 올라갑니다. 스케줄은 워크플로 설정(탐색기 ⚙)의 cron 을 따르고, 이 연습은 수동 실행만 합니다.',
      doneText: '「DAG 코드」를 눌러 배포된 코드도 한번 보세요.',
    },
    run: {
      title: '실행',
      text: '도구 모음의 「실행」(▶ 아이콘)을 누르면 배포된 DAG 를 트리거하고, 끝날 때까지 캔버스 노드에 태스크 상태 뱃지가 붙습니다. 캔버스가 ${변수}를 쓰면 매개변수 창이 먼저 뜹니다.\n「대신 해줘」를 누르면 트리거하고 끝날 때까지(최대 3분) 진행을 보여 드립니다.',
      doneText: '노드의 상태 뱃지를 누르면 그 태스크의 로그가 열립니다.',
    },
    verifyTitle: '결과 확인',
    finishTitle: '마무리',
  },

  tracks: {
    basic: {
      label: '입문 — SAM파일 → DB 적재',
      workflow: '퀵스타트 입문',
      workflowDesc: 'Quick Start 입문 연습용',
      folder: '연습',
      nodes: { orderFile: '주문 파일', process: '데이터처리', targetOrders: '타깃 주문' },
      steps: [
        {
          title: '새 워크플로 만들기',
          text: '이 프로젝트에 「퀵스타트 입문」 워크플로를 만들어 열었습니다 — 탐색기의 + 와 같습니다. 캔버스는 비어 있습니다.\n여기에 SAM 파일을 읽어 DB 테이블에 적재하는 가장 기본적인 흐름(샘플①과 같은 것)을 만들어 보겠습니다.',
        },
        {
          title: '소스 데이터 노드',
          text: '왼쪽 툴박스 데이터 › 「SAM파일」을 캔버스로 끌어 놓으세요. 노드를 클릭하면 오른쪽 「노드 속성」에서 파일 경로(orders.csv)·구분자·헤더를 정합니다.\n「대신 해줘」를 누르면 샘플①과 같은 설정으로 넣어 드립니다.',
          doneText: '「주문 파일」(SAM파일, orders.csv) 노드를 넣었습니다. 캔버스와 오른쪽 노드 속성을 확인해 보세요.',
        },
        {
          title: '처리 노드와 연결',
          text: '툴박스 프로세스 › 「데이터처리」를 오른쪽에 놓고, 「주문 파일」 오른쪽 점에서 「데이터처리」 로 드래그해 이으세요.\n데이터 → 처리 순서입니다. 캔버스 위 「DFD 검사」 줄이 이 규칙을 확인해 줍니다.',
          doneText: '「데이터처리」 노드를 넣고 「주문 파일」 → 「데이터처리」 로 이었습니다.',
        },
        {
          title: '타깃 데이터 노드',
          text: '툴박스 데이터 › 「DB테이블」을 맨 오른쪽에 놓고 「데이터처리」 → 「DB테이블」 로 이으세요. 노드 속성에서 mode 를 WRITE 로, 연결·테이블을 고릅니다.\n이제 데이터 → 처리 → 데이터 가 갖춰져 DFD 검사가 「적합」이 됩니다.',
          doneText: '「타깃 주문」(DB테이블, of_test_tgt_orders_csv) 노드를 넣고 이었습니다. DFD 검사가 적합인지 캔버스 위 줄을 보세요.',
        },
        {
          title: '매핑',
          text: '「데이터처리」 노드를 클릭 → 오른쪽 노드 속성 → 매핑 창을 여세요. 「매핑 제안」이 소스·타깃 항목을 이름으로 짝지어 주고, 이름이 달라 못 맞춘 것은 「✨ AI 보조」로 어시스턴트에게 물어볼 수 있습니다.\n이 연습은 orders.csv 와 타깃 테이블의 항목 이름이 같아 변환 모드 Move(그대로 옮김)로 충분합니다 — 매핑을 비워 두어도 됩니다.',
        },
      ],
      verify: {
        title: '결과 확인',
        text: '적재가 됐는지 타깃 테이블을 확인합니다. 「데이터 정의」 › DB테이블에서 of_test_tgt_orders_csv 를 미리보기 하거나, 도구 모음의 「실행 이력」에서 태스크별 로그를 봅니다.\norders.csv 가 7행이므로 7행이 들어 있어야 합니다(데이터처리 mode WRITE 는 적재 전에 TRUNCATE 하므로 다시 실행해도 7행).',
        doneText: '실행 탭에서도 이 워크플로의 실행 이력·타임라인을 볼 수 있습니다.',
      },
      finish: '기본 흐름(설계 → 저장 → 배포 → 실행 → 확인)을 다 해 보셨습니다. 실행 탭은 배포된 워크플로의 조회·실행 전용이고, 편집은 언제나 디자이너에서 합니다.\n연습 워크플로는 그대로 두어도 되고, 탐색기에서 지워도 됩니다(다시 돌리면 캔버스를 비우고 시작합니다).\n\n다음은 「중급 — 분기·합류」입니다: 조건으로 흐름을 나누고 다시 모읍니다.',
    },

    inter: {
      label: '중급 — 분기·합류',
      workflow: '퀵스타트 중급',
      workflowDesc: 'Quick Start 중급 연습용 — 분기/합류',
      folder: '연습',
      nodes: {
        srcOrders: '소스 주문', extract: '추출', orderData: '주문 데이터',
        branch: '고액 있음?', filterHigh: '고액만', highOrders: '고액 주문',
        passAll: '전체 그대로', allOrders: '전체 주문', merge: '합류', load: '적재', branchResult: '분기 결과',
      },
      steps: [
        {
          title: '새 워크플로 만들기',
          text: '「퀵스타트 중급」 워크플로를 만들어 열었습니다.\n이번에는 DB 테이블에서 주문을 읽어 금액 조건으로 흐름을 나누고(분기), 다시 모아(합류) 적재합니다 — 샘플③과 같은 흐름입니다. 새로 나오는 것: DB 소스, 내부데이터, 분기의 TRUE/FALSE 핸들, 필터, 합류.',
        },
        {
          title: 'DB 소스 → 추출 → 내부데이터',
          text: '툴박스 데이터 › 「DB테이블」(소스 주문, of_test_src_orders, mode READ) → 프로세스 › 「데이터처리」(추출) → 데이터 › 「내부데이터」(주문 데이터) 순으로 놓고 이으세요.\n내부데이터는 처리 결과를 다음 처리로 넘기는 메모리 데이터셋입니다. 처리와 처리 사이에는 반드시 데이터 노드가 있어야 하므로(DFD 규칙) 자주 씁니다.',
          doneText: '소스 주문 → 추출 → 주문 데이터 를 넣고 이었습니다.',
        },
        {
          title: '분기 노드',
          text: '툴박스 컨트롤 › 「분기」를 「주문 데이터」 오른쪽에 놓고 이으세요. 노드 속성에서 condition 을 amount > 1000000 으로, defaultPath 를 FALSE 로 정합니다.\n분기는 오른쪽 핸들이 TRUE, 아래 핸들이 FALSE 입니다. 조건이 참인 행이 하나라도 있으면 TRUE 길로 갑니다.',
          doneText: '「고액 있음?」 분기 노드를 넣고 이었습니다. 노드를 클릭해 condition 을 확인해 보세요.',
        },
        {
          title: 'TRUE 갈래 — 필터',
          text: '분기의 오른쪽 핸들(TRUE)에서 프로세스 › 「필터」(고액만, condition amount > 1000000) 로 잇고, 그 뒤에 「내부데이터」(고액 주문)를 두세요. 위쪽에 놓으면 보기 좋습니다.\nFilter 는 조건에 맞는 행만 남깁니다.',
          doneText: 'TRUE 갈래(고액만 → 고액 주문)를 이었습니다.',
        },
        {
          title: 'FALSE 갈래 — 그대로',
          text: '분기의 아래 핸들(FALSE)에서 「데이터처리」(전체 그대로, Move) → 「내부데이터」(전체 주문) 로 잇고 아래쪽에 두세요.\n조건이 거짓이면 이 길로 갑니다. 「대신 해줘」는 아래 핸들(false-path)로 잇습니다.',
          doneText: 'FALSE 갈래(전체 그대로 → 전체 주문)를 아래 핸들로 이었습니다.',
        },
        {
          title: '합류 → 적재 → 타깃',
          text: '컨트롤 › 「합류」를 두 갈래 오른쪽에 놓고 「고액 주문」·「전체 주문」 둘 다 합류로 이으세요. 그 뒤에 「데이터처리」(적재) → 「DB테이블」(분기 결과, of_test_tgt_branch, WRITE).\n합류는 어느 갈래가 건너뛰어져도 이어서 실행되도록 trigger_rule 이 붙습니다. DFD 검사가 「적합」인지 보세요.',
          doneText: '합류 → 적재 → 분기 결과 까지 이었습니다. 왼쪽 아래 ⤢ 로 전체를 맞춰 보세요.',
        },
      ],
      verify: {
        title: '결과 확인',
        text: '소스 of_test_src_orders 에는 amount 가 1,000,000 을 넘는 주문이 1건(1005) 있습니다. 그래서 분기는 TRUE 길로 가고 필터가 그 1건만 남겨 of_test_tgt_branch 에 1행이 적재됩니다.\n캔버스의 노드 뱃지를 보면 FALSE 갈래는 「건너뜀」으로 표시됩니다.',
        doneText: '「실행 이력」에서 건너뛴 태스크(skipped)와 합류의 trigger_rule 을 확인해 보세요.',
      },
      finish: '분기·합류를 다 해 보셨습니다. 조건을 amount > 100 처럼 바꾸면 TRUE 길로 가되 필터가 더 많은 행을 남기고, 아무 행도 맞지 않는 조건이면 FALSE 길로 갑니다 — 바꿔서 다시 저장·배포·실행해 보세요.\n\n다음은 「고급 — 품질검사·위반 분리」입니다: 규칙으로 행을 검사해 통과와 위반을 다른 곳에 씁니다.',
    },

    adv: {
      label: '고급 — 품질검사·위반 분리',
      workflow: '퀵스타트 고급',
      workflowDesc: 'Quick Start 고급 연습용 — 품질검사',
      folder: '연습',
      nodes: {
        srcOrders: '소스 주문', extract: '추출', orderData: '주문 데이터',
        quality: '주문 품질검사', passOrders: '통과 주문', load: '적재', targetOrders: '타깃 주문',
        rejectOrders: '위반 주문', rejectLog: '위반 기록', rejectFile: '위반 파일',
        ruleNoCustomer: '고객명 없음', ruleAmountRange: '금액 범위 초과',
      },
      steps: [
        {
          title: '새 워크플로 만들기',
          text: '「퀵스타트 고급」 워크플로를 만들어 열었습니다.\n이번에는 주문을 규칙(필수·허용값·범위·유일)으로 검사해 통과한 행은 DB 에, 위반한 행은 파일에 따로 씁니다 — 샘플⑩과 같은 흐름입니다. 새로 나오는 것: 품질검사 노드와 규칙, 위반(reject) 핸들, SAM파일 쓰기.',
        },
        {
          title: 'DB 소스 → 추출 → 내부데이터',
          text: '중급과 같습니다: 「DB테이블」(소스 주문, of_test_src_orders) → 「데이터처리」(추출) → 「내부데이터」(주문 데이터).',
          doneText: '소스 주문 → 추출 → 주문 데이터 를 넣고 이었습니다.',
        },
        {
          title: '품질검사 노드와 규칙',
          text: '프로세스 › 「품질검사」를 「주문 데이터」 오른쪽에 놓고 이으세요. 노드 속성에서 규칙을 넣습니다: customer 필수(not_null), status 허용값 A,C(in_list), amount 범위 0~500000(range), order_id 유일(unique). onViolation 은 reject(위반 행을 따로 뺌).\n「규칙 제안」을 누르면 앞 노드의 컬럼·샘플을 보고 규칙을 제안해 줍니다.',
          doneText: '「주문 품질검사」 노드에 규칙 4개를 넣고 이었습니다. 노드를 클릭해 규칙 목록을 보세요.',
        },
        {
          title: '통과 경로 → DB',
          text: '품질검사의 오른쪽 핸들(통과)에서 「내부데이터」(통과 주문) → 「데이터처리」(적재) → 「DB테이블」(타깃 주문, of_test_tgt_orders, WRITE) 로 이으세요. 위쪽에 두면 보기 좋습니다.',
          doneText: '통과 경로(통과 주문 → 적재 → 타깃 주문)를 이었습니다.',
        },
        {
          title: '위반 경로 → 파일',
          text: '품질검사의 아래 핸들(위반, reject)에서 「내부데이터」(위반 주문) → 「데이터처리」(위반 기록) → 「SAM파일」(위반 파일, out/rejects.csv, mode WRITE) 로 이으세요.\nSAM파일 노드도 mode 를 WRITE 로 두면 출력 파일이 됩니다. 위반 행에는 어떤 규칙에 걸렸는지(_violations)가 함께 기록됩니다.',
          doneText: '위반 경로(위반 주문 → 위반 기록 → 위반 파일)를 reject 핸들로 이었습니다. DFD 검사가 「적합」인지 보세요.',
        },
      ],
      verify: {
        title: '결과 확인',
        text: '소스 7행 중 amount 가 1,200,000 인 주문 1건(1005)이 범위 규칙에 걸립니다. 그래서 of_test_tgt_orders 에 6행, out/rejects.csv 에 1행이 있어야 합니다.\n위반 파일은 Airflow 쪽 폴더(backend/dags/samples/out)에 쓰이며, 「서버 폴더」 메뉴에서 볼 수 있습니다.',
        doneText: '「실행 이력」에서 품질검사 태스크 로그를 열면 규칙별 위반 건수가 보입니다.',
      },
      finish: '품질검사와 위반 분리를 다 해 보셨습니다. 규칙의 range 를 0,2000000 으로 바꾸면 위반이 0건이 되고, onViolation 을 FAIL 로 바꾸면 위반이 있을 때 실행이 실패로 멈춥니다 — 바꿔서 다시 돌려 보세요.\n\n더 나아가기: 반복그룹(샘플⑤), 서브플로·사용자 확인(샘플④), 실행 노드(샘플⑧), 룩업·집계·UPSERT(샘플⑨)는 「주제별 안내 보기」에서 샘플을 열어 설명합니다.',
    },
  },

  triggers: { help: '도움말', usageOf: '{{topic}} 사용법 알려줘' },
};

export default ko;
